/**
 * Pagination dots for the products slider: count and targets depend on viewport (slides per view).
 * Rebuilds dots when the window crosses tablet/desktop breakpoints.
 */
const BP_TABLET = 750;
const BP_DESKTOP = 990;
const BP_WIDE = 1400;

function getSlidesToShow(root) {
  const wide = parseInt(String(root.dataset.slidesWide || '5'), 10);
  const d = parseInt(String(root.dataset.slidesDesktop || '4'), 10);
  const t = parseInt(String(root.dataset.slidesTablet || '2'), 10);
  const m = parseInt(String(root.dataset.slidesMobile || '1'), 10);
  const w = typeof window !== 'undefined' ? window.innerWidth : BP_DESKTOP;
  if (w >= BP_WIDE) return Math.max(1, Number.isFinite(wide) ? wide : 5);
  if (w >= BP_DESKTOP) return Math.max(1, Number.isFinite(d) ? d : 4);
  if (w >= BP_TABLET) return Math.max(1, Number.isFinite(t) ? t : 2);
  return Math.max(1, Number.isFinite(m) ? m : 1);
}

function applyDots(dots, pageIndex) {
  const max = dots.length - 1;
  const safe = Math.min(max, Math.max(0, pageIndex));
  dots.forEach((dot, i) => {
    const active = i === safe;
    dot.classList.toggle('products-slider__dot--active', active);
    dot.setAttribute('aria-current', active ? 'true' : 'false');
  });
}

/** Scroll offset so `slide` aligns with the track’s left edge (offsetLeft is unreliable vs flex/gap). */
function getScrollLeftForSlide(track, slide) {
  const maxScroll = Math.max(0, track.scrollWidth - track.clientWidth);
  let left = slide.getBoundingClientRect().left - track.getBoundingClientRect().left + track.scrollLeft;
  left = Math.max(0, Math.min(maxScroll, Math.round(left)));
  return left;
}

function computePageFromScroll(root) {
  const track = root.querySelector('.products-slider__track');
  const dots = root.querySelectorAll('.products-slider__progress .products-slider__dot');
  const slides = track ? root.querySelectorAll('.products-slider__slide') : [];
  const n = getSlidesToShow(root);
  if (!track || dots.length === 0 || slides.length === 0) return null;

  const scrollLeft = track.scrollLeft;
  let leftSlideIndex = 0;
  for (let i = 0; i < slides.length; i++) {
    const slideStart = getScrollLeftForSlide(track, slides[i]);
    if (slideStart <= scrollLeft + 12) {
      leftSlideIndex = i;
    }
  }

  return Math.min(dots.length - 1, Math.max(0, Math.floor(leftSlideIndex / n)));
}

function buildDots(root, nav) {
  const productCount = parseInt(String(root.dataset.productCount || '0'), 10);
  const sectionId = root.dataset.sectionId;
  const n = getSlidesToShow(root);
  if (!sectionId || productCount < 1) {
    nav.innerHTML = '';
    return;
  }

  const dotCount = Math.ceil(productCount / n);
  const frag = document.createDocumentFragment();
  for (let i = 0; i < dotCount; i++) {
    const pageFirst = i * n;
    const a = document.createElement('a');
    a.className = 'products-slider__dot' + (i === 0 ? ' products-slider__dot--active' : '');
    a.href = '#ProductsSlider-Slide-' + sectionId + '-' + pageFirst;
    a.dataset.pageIndex = String(i);
    a.setAttribute('aria-current', i === 0 ? 'true' : 'false');
    a.setAttribute('aria-label', 'Slide ' + (i + 1) + ' of ' + dotCount);
    frag.appendChild(a);
  }
  nav.innerHTML = '';
  nav.appendChild(frag);
}

function init(root) {
  const track = root.querySelector('.products-slider__track');
  const nav = root.querySelector('.products-slider__progress[data-products-slider-dots]');
  if (!track || !nav) return;

  let debounceTimer;
  let resizeDebounce;
  let dotNavGuard = null;
  let lastSlidesN = null;
  let lastDotCount = -1;

  const getDots = () => root.querySelectorAll('.products-slider__progress .products-slider__dot');

  const safeSyncFromScroll = () => {
    const dots = Array.from(getDots());
    if (dots.length === 0) return;

    const page = computePageFromScroll(root);
    if (page === null) return;

    if (dotNavGuard) {
      const { expected, until } = dotNavGuard;
      if (Date.now() < until && page !== expected) {
        return;
      }
      dotNavGuard = null;
    }

    applyDots(dots, page);
  };

  const debouncedSync = () => {
    clearTimeout(debounceTimer);
    debounceTimer = window.setTimeout(safeSyncFromScroll, 50);
  };

  const rebuildDotsIfNeeded = () => {
    const n = getSlidesToShow(root);
    const productCount = parseInt(String(root.dataset.productCount || '0'), 10);
    const dotCount = productCount > 0 ? Math.ceil(productCount / n) : 0;
    if (n === lastSlidesN && dotCount === lastDotCount && getDots().length === dotCount) {
      return;
    }
    lastSlidesN = n;
    lastDotCount = dotCount;
    dotNavGuard = null;
    buildDots(root, nav);
    safeSyncFromScroll();
  };

  nav.addEventListener('click', (e) => {
    const dot = e.target.closest('.products-slider__dot');
    if (!dot || !nav.contains(dot)) return;
    const p = parseInt(String(dot.dataset.pageIndex ?? ''), 10);
    if (!Number.isFinite(p)) return;
    e.preventDefault();

    const n = getSlidesToShow(root);
    const slideIndex = p * n;
    const slides = track.querySelectorAll('.products-slider__slide');
    const target = slides[slideIndex];
    if (target) {
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const left = getScrollLeftForSlide(track, target);
      track.style.setProperty('scroll-snap-type', 'none');

      let snapRestored = false;
      const restoreSnap = () => {
        if (snapRestored) return;
        snapRestored = true;
        track.style.removeProperty('scroll-snap-type');
      };

      track.addEventListener('scrollend', restoreSnap, { once: true });
      window.setTimeout(restoreSnap, reduceMotion ? 80 : 800);

      track.scrollTo({
        left,
        behavior: reduceMotion ? 'auto' : 'smooth',
      });
    }

    clearTimeout(debounceTimer);
    const dots = Array.from(getDots());
    dotNavGuard = { expected: p, until: Date.now() + 900 };
    applyDots(dots, p);
  });

  track.addEventListener('scroll', debouncedSync, { passive: true });
  track.addEventListener('scrollend', safeSyncFromScroll);
  window.addEventListener('hashchange', () => {
    window.setTimeout(safeSyncFromScroll, 0);
    window.setTimeout(safeSyncFromScroll, 120);
  });
  window.addEventListener('resize', () => {
    dotNavGuard = null;
    clearTimeout(resizeDebounce);
    resizeDebounce = window.setTimeout(() => {
      rebuildDotsIfNeeded();
      debouncedSync();
    }, 100);
  });

  rebuildDotsIfNeeded();
  safeSyncFromScroll();
  requestAnimationFrame(safeSyncFromScroll);
}

function boot() {
  document.querySelectorAll('.products-slider__component[data-section-id]').forEach(init);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot);
} else {
  boot();
}
